import { useEffect, useState } from 'react';
import { GetServerSideProps } from 'next';
import { XMLParser } from 'fast-xml-parser';

import client from 'api/apollo-client';
import { AgendaPageFieldsFragment, PageInformationFieldsFragment } from 'api/graphql-types';
import { getServerPageGetAgenda } from 'api/graphql-helper';

import { ROUTES } from 'constants/routes';
import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { mapXmlToAccordionList } from 'utils/xml-helper';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { AccordionList, IAccordionListProps } from 'modules/accordion-list';
import { Head } from 'modules/head/head';
import { BaseModule } from 'modules/base-module/base-module';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import Layout from 'modules/layout/layout';
import IntroHeader from 'modules/intro-header/intro-header';
import { getXMLAgenda } from 'utils/aws-s3-helper';

interface IAgendaPageProps extends IBasePageProps {
	xml?: any;
	pageInformation?: PageInformationFieldsFragment;
	agendaPage?: AgendaPageFieldsFragment;
	id?: string;
}

const AgendaPage = ({ pageInformation, agendaPage, xml, id }: IAgendaPageProps): React.JSX.Element => {
	const { modules } = { ...agendaPage };
	const seoModule = agendaPage?.seo;
	const [accordionData, setAccordionData] = useState<IAccordionListProps | undefined>(undefined);

	useEffect(() => {
		if (xml && xml.Besluitenlijst) {
			setAccordionData(mapXmlToAccordionList(xml.Besluitenlijst));
		}
	}, [xml]);

	return (
		<Layout
			contentId={id}
			typeName={agendaPage?.__typename}
			navbar={pageInformation?.navbar}
			footer={pageInformation?.footer}
		>
			<Head seoModule={seoModule} seoParent={pageInformation?.seo} />
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: agendaPage?.header?.backgroundColor || 'white',
					padding: 'none'
				}}
			>
				<IntroHeader module={agendaPage?.header} slug={ROUTES.AGENDA.fullPath} />
			</BaseModule>

			{accordionData && (
				<BaseModule
					defaultModuleOptions={{
						backgroundColor: agendaPage?.header?.backgroundColor === 'white' ? 'grey' : 'white',
						padding: 'medium'
					}}
				>
					<AccordionList {...accordionData} />
				</BaseModule>
			)}

			<ModuleBuilder modules={modules} slug={ROUTES.AGENDA.fullPath} />
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);
		const xml = await getXMLAgenda();

		const result = await getServerPageGetAgenda(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);
		const { pageInformation, agendaPage } = result.props.data;

		if (agendaPage == null) {
			return {
				notFound: true
			};
		}

		const parser = new XMLParser();
		const parsedXml = xml ? parser.parse(xml) : '';

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes,
				agendaPage: agendaPage.data?.attributes,
				xml: parsedXml,
				id: agendaPage.data?.documentId,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'agenda-page',
						contentTypeId: 1
					}
				}
			}
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in agenda page', error);

		return {
			notFound: true
		};
	}
};

export default AgendaPage;
